import { ConditionalRender } from '@libs/core';
import { TButtonData, TWidget, TViewCommonOption } from '@libs/types';
import { CoreButtonView, CoreTextView, TCoreButtonStyleProps, TCoreTextStyleProps, type TCoreButtonViewProps } from '@core/widgets';
import React, { ElementType, MouseEvent, MouseEventHandler } from 'react';
import { TButtonOption } from '../../types/options';
import { VisualView } from '../Image/VisualView';

type TButtonRowStyleProps = Pick<TButtonOption, 'margin' | 'iconName' | 'iconVariant' | 'size' | 'alignment' | 'hierarchy'>;

export type TButtonRowViewProps = {
    widget?: TWidget<TButtonData, TButtonOption>;
    uid?: string;
    label?: string;
    as?: ElementType;
    /**
     * Called when the button is clicked. Optional in the standalone (non-widget) form
     * when the element is used as a non-interactive label (e.g. `as="div"`).
     */
    onClick?: MouseEventHandler;
    children?: React.ReactNode;
} & Pick<TViewCommonOption, 'isDisabled' | 'isHide' | 'parentStyleNames'> &
    TButtonRowStyleProps;

type THierarchy = NonNullable<TButtonOption['hierarchy']>;

const HIERARCHY_COLOR_MAP: Record<THierarchy, { normal: string; disabled: string }> = {
    highlighted: {
        normal: 'var(--color-purple_100)',
        disabled: 'var(--color-purple_40_tr)',
    },
    primary: {
        normal: 'var(--color-purple_100)',
        disabled: 'var(--color-purple_40_tr)',
    },
    secondary: {
        normal: 'var(--color-purple_100)',
        disabled: 'var(--color-purple_40_tr)',
    },
    tertiary: {
        normal: 'var(--color-purple_100)',
        disabled: 'var(--color-purple_40_tr)',
    },
    destructive: {
        normal: 'var(--color-pink_100)',
        disabled: 'var(--color-pink_40_lc)',
    },
};

const HIERARCHY_BG_MAP: Record<THierarchy, Pick<TCoreButtonStyleProps, 'main' | 'disabled' | 'pressed' | 'hovered'>> = {
    highlighted: {
        main: 'var(--color-white_0_tr_base)',
        hovered: {
            main: 'var(--color-purple_16_tr)',
        },
        pressed: {
            main: 'var(--color-purple_8_tr)',
        },
        disabled: {
            main: 'var(--color-white_0_tr_base)',
        },
    },
    primary: {
        main: 'var(--color-white_0_tr_base)',
        hovered: {
            main: 'var(--color-purple_16_tr)',
        },
        pressed: {
            main: 'var(--color-purple_8_tr)',
        },
        disabled: {
            main: 'var(--color-white_0_tr_base)',
        },
    },
    destructive: {
        main: 'var(--color-white_0_tr_base)',
        hovered: {
            main: 'var(--color-pink_16_tr)',
        },
        pressed: {
            main: 'var(--color-pink_8_tr)',
        },
        disabled: {
            main: 'var(--color-white_0_tr_base)',
        },
    },
    secondary: {
        main: 'var(--color-white_0_tr_base)',
        hovered: {
            main: 'var(--color-purple_16_tr)',
        },
        pressed: {
            main: 'var(--color-purple_8_tr)',
        },
        disabled: {
            main: 'var(--color-white_0_tr_base)',
        },
    },
    tertiary: {
        main: 'var(--color-white_0_tr_base)',
        hovered: {
            main: 'var(--color-purple_16_tr)',
        },
        pressed: {
            main: 'var(--color-purple_8_tr)',
        },
        disabled: {
            main: 'var(--color-white_0_tr_base)',
        },
    },
};

const SIZE_PADDING_MAP: Record<string, Pick<TCoreButtonStyleProps, 'padding'>> = {
    large: {
        padding: { top: 24, right: 16, bottom: 24, left: 24 },
    },
    largeWithIcon: {
        padding: { top: 16, right: 24, bottom: 16, left: 16 },
    },
    small: {
        padding: { top: 16, right: 16, bottom: 16, left: 16 },
    },
    smallWithIcon: {
        padding: { top: 8, right: 24, bottom: 8, left: 16 },
    },
};

/**
 * Composable building block for the ButtonRow widget.
 * Accepts pre-resolved behavior props (isDisabled, isHide, onClick) from the parent,
 * making it suitable for scenarios where behavior state is already managed one level up.
 * Delegates theme resolution and all rendering to CoreButtonView.
 */
export const ButtonRowView = ({
    uid,
    widget,
    label,
    isDisabled,
    isHide,
    onClick,
    as = 'button',
    parentStyleNames,
    iconName,
    iconVariant = 'simple',
    size,
    alignment,
    hierarchy = 'primary' as THierarchy,
    margin,
}: TButtonRowViewProps) => {
    const resolvedParentStyleNames = parentStyleNames ?? [];

    const hasSizeKey = size === 'large' ? (iconName ? 'largeWithIcon' : 'large') : iconName ? 'smallWithIcon' : 'small';

    const colorState = isDisabled ? 'disabled' : 'normal';
    const hierarchyColors = HIERARCHY_COLOR_MAP[hierarchy];
    const textColor = hierarchyColors?.[colorState];

    const coreButtonStyleProps: TCoreButtonStyleProps = {
        ...HIERARCHY_BG_MAP[hierarchy],
        ...SIZE_PADDING_MAP[hasSizeKey],
        onMain: textColor,
        fillContainer: true,
        margin: margin ?? {},
        borderRadius: 0,
        fontStyle: { fontSize: 17, lineHeight: 24 },
        cursorDisabled: 'default',
        textAlign: (['center', 'start', 'end'] as const).find((v) => v === alignment) ?? 'start',
        gap: alignment === 'center' ? 8 : 16,
        disabled: {
            ...HIERARCHY_BG_MAP[hierarchy].disabled,
            onMain: hierarchyColors?.disabled,
        },
        hovered: {
            ...HIERARCHY_BG_MAP[hierarchy].hovered,
            onMain: textColor,
        },
        pressed: {
            ...HIERARCHY_BG_MAP[hierarchy].pressed,
            onMain: textColor,
        },
    };

    const coreTextStyleProps: TCoreTextStyleProps = {
        main: textColor,
    };

    return (
        <CoreButtonView
            uid={uid}
            widget={widget as TCoreButtonViewProps['widget']}
            text={label}
            isDisabled={isDisabled}
            isHide={isHide}
            styleProps={coreButtonStyleProps}
            parentStyleNames={resolvedParentStyleNames}
            as={as}
            styleName="rowButton"
            onClick={(e: MouseEvent<HTMLButtonElement>) => {
                onClick?.(e);
            }}
        >
            <ConditionalRender condition={!!iconName}>
                <VisualView
                    parentStyleNames={[...resolvedParentStyleNames, 'rowButtonIcon']}
                    isDisabled={isDisabled}
                    iconName={iconName}
                    iconVariant={iconVariant}
                    iconHierarchy={hierarchy === 'destructive' ? 'destructive' : undefined}
                />
            </ConditionalRender>
            <CoreTextView text={label} as="span" parentStyleNames={resolvedParentStyleNames} styleName="rowButtonText" styleProps={coreTextStyleProps} />
        </CoreButtonView>
    );
};
